import {
  getHash,
  getMerchant,
  getReceipts,
  getSafePaymentTypeCode,
  getSafePaymentType,
} from '@tectonics/prs/incoming-payment';
import type { IncomingPayment } from '@tectonics/prs/incoming-payment';
import { getMainReceipt } from '@tectonics/prs/receipts';
import { getReceiptAmountExt } from '@tectonics/prs/receipts/receipt';
import { getAmountValue, getAmountCurrencyCode } from '@tectonics/prs/money';
import type { Amount } from '@tectonics/prs/money';
import { getFacebookPixelIds } from '@tectonics/prs/merchant';
import { AnalyticsProvider } from '../types';
import type { AnalyticsEventType } from '../types';

export class FacebookPixel extends AnalyticsProvider {
  init(incomingPayment: IncomingPayment): boolean {
    super.init(incomingPayment);

    if (!window.fbq) return false;

    const merchant = getMerchant(incomingPayment);
    const pixels = getFacebookPixelIds(merchant);
    pixels.forEach(pixel => window.fbq!('init', `${pixel}`));
    return true;
  }

  event(eventType: AnalyticsEventType): void {
    switch (eventType) {
      case 'pageView':
        this.onPageView();
        break;

      case 'purchase':
        this.onPurchase();
        break;

      case 'purchaseAttempt':
        this.onPurchaseAttempt();
        break;

      case 'voucherGenerate':
        this.onVoucherGenerate();
        break;
    }
  }

  eventOnce(eventType: AnalyticsEventType, incomingPayment: IncomingPayment): void {
    const hash = getHash(incomingPayment);
    if (!hasBeenSent(hash)) {
      this.event(eventType);
      saveHasBeenSent(hash);
    }
  }

  onPageView(): void {
    window.fbq!('track', 'PageView');
  }

  onPurchase(): void {
    const amount = this.getAmount();

    window.fbq!('track', 'Purchase', {
      value: getAmountValue(amount),
      currency: getAmountCurrencyCode(amount),
      payment_type_code: this.userInputValues['selectedPaymentMethod'],
    });
  }

  onPurchaseAttempt(): void {
    const amount = this.getAmount();
    window.fbq!('trackCustom', 'PurchaseAttempt', {
      value: getAmountValue(amount),
      currency: getAmountCurrencyCode(amount),
      payment_type_code: getSafePaymentType(this.incomingPayment),
    });
  }

  onVoucherGenerate(): void {
    const paymentTypeCode = getSafePaymentTypeCode(this.incomingPayment);
    const amount = this.getAmount();
    window.fbq!('trackCustom', 'VoucherGenerate', {
      value: getAmountValue(amount),
      currency: getAmountCurrencyCode(amount),
      payment_type_code: paymentTypeCode,
    });
  }

  getAmount(): Amount {
    const incomingPayment = this.incomingPayment;
    const receipts = getReceipts(incomingPayment);
    const receipt = getMainReceipt(receipts);
    return getReceiptAmountExt(receipt);
  }
}

function saveHasBeenSent(hash: string): void {
  localStorage.setItem(getHasBeenSentKey(hash), 'yes');
}

function hasBeenSent(hash: string): boolean {
  return localStorage.getItem(getHasBeenSentKey(hash)) === 'yes';
}

function getHasBeenSentKey(hash: string): string {
  return `tectonics:fbq:${hash}:has_been_sent`;
}
